// useTestimonial.ts
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue'
import { useWindowSize } from '@vueuse/core'
import Swiper from 'swiper'
import { Autoplay } from 'swiper/modules'

export interface QuoteBoxItem {
	text: string
	name: string
}

export function useTestimonial() {
	const { width } = useWindowSize()

	const marginStyle = computed(() => {
		const DESKTOP_BREAKPOINT = 1280
		const CONTAINER_WIDTH = 1024
		const MAX_MARGIN = 78
		if (width.value < DESKTOP_BREAKPOINT) return ''

		const margin =
			Math.min((width.value - CONTAINER_WIDTH) / 2, MAX_MARGIN) * -1
		return `margin-left: ${margin}px !important; margin-right: ${margin}px !important;`
	})

	const swiperEl = ref<HTMLElement | null>(null)
	let swiper: Swiper | null = null

	const goPrev = () => {
		swiper?.slidePrev(500)
	}

	const goNext = () => {
		swiper?.slideNext(500)
	}

	const setActiveSlide = () => {
		if (!swiper) return
		const activeSlide = swiper.slides[swiper.activeIndex]
		activeSlide.classList.add('active')
	}

	const handleSlideChangeTransitionStart = () => {
		if (!swiper) return
		const previousActiveSlide = swiper.slides[swiper.previousIndex]
		previousActiveSlide.classList.remove('active')
	}

	const handleSlideChangeTransitionEnd = () => {
		setActiveSlide()
	}

	const activateInitialSlide = () => {
		setActiveSlide()
	}

	const swiperOptions = {
		modules: [Autoplay],
		autoplay: {
			delay: 5000,
		},
		loop: true,
		slidesPerView: 'auto' as 'auto',
		spaceBetween: 32,
		centeredSlides: true,
		speed: 1000,
		allowTouchMove: false,
		on: {
			slideChangeTransitionStart: handleSlideChangeTransitionStart,
			slideChangeTransitionEnd: handleSlideChangeTransitionEnd,
		},
	}

	const initializeSwiper = async () => {
		if (!swiperEl.value) return
		swiper = new Swiper(swiperEl.value, swiperOptions)
		activateInitialSlide()
	}

	onMounted(async () => {
		await nextTick()
		initializeSwiper()
	})

	onUnmounted(() => {
		swiper?.destroy()
	})

	return { swiperEl, marginStyle, goPrev, goNext }
}
